import { CLIENT_ID, CLIENT_ID_HEADER } from "./clientId";
import { API_BASE } from "./config";
import { bounceIfUnauthorized } from "./session";

/** Decks and tags — the two Setup panels that administer the collection. */

export interface DeckRow {
  id: number;
  name: string;
  /** `::` nesting depth. The panel indents by `depth * 22px`. */
  depth: number;
  new: number;
  due: number;
  total: number;
  /**
   * What the scheduler would hand you for this deck today — limits applied, parents over children.
   * Unlike `new`/`due`/`total`, which are self-only so the indented tree does not double-count,
   * this one **includes subdecks**, because a tree node's counts always do.
   */
  dueToday: number;
  newLimit: number;
  reviewLimit: number;
  /** A parent deck. Deck options are not offered on one (日本-344). */
  hasSubdecks: boolean;
}

export interface TagRow {
  name: string;
  count: number;
  /** Written OR read by the app — renaming it would break that, and the server refuses. */
  machine: boolean;
}

async function errorMessage(res: Response): Promise<string> {
  bounceIfUnauthorized(res);
  try {
    const body = await res.json();
    if (typeof body?.detail === "string") return body.detail;
    if (typeof body?.error === "string") return body.error;
  } catch {
    // non-JSON body
  }
  return `Request failed (${res.status})`;
}

async function send<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      // Says "this tab has already accounted for this change locally", so the collectionChanged
      // frame it provokes is ignored here and acted on everywhere else. See `clientId.ts`.
      [CLIENT_ID_HEADER]: CLIENT_ID,
      ...(body === undefined ? {} : { "Content-Type": "application/json" }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
    cache: "no-store",
  });
  if (!res.ok) throw new Error(await errorMessage(res));
  return res.json();
}

type Decks = { decks: DeckRow[] };
type Tags = { tags: TagRow[] };

export const listSetupDecks = () => send<Decks>("GET", "/api/setup/decks");
export const createDeck = (name: string) => send<Decks>("POST", "/api/setup/decks", { name });
export const renameDeck = (id: number, name: string) =>
  send<Decks>("PATCH", `/api/setup/decks/${id}`, { name });
export const deleteDeck = (id: number) =>
  send<{ count: number }>("DELETE", `/api/setup/decks/${id}`);
export const reparentDecks = (deckIds: number[], parentId: number | null) =>
  send<Decks>("POST", "/api/setup/decks/reparent", { deckIds, parentId });

export const listTags = () => send<Tags>("GET", "/api/setup/tags");
export const renameTag = (oldName: string, newName: string) =>
  send<Tags>("POST", "/api/setup/tags/rename", { old: oldName, new: newName });
export const deleteTag = (tag: string) => send<Tags>("POST", "/api/setup/tags/delete", { tag });
export const clearUnusedTags = () => send<Tags>("POST", "/api/setup/tags/clear-unused");
